import { getProjectClientCapabilities } from '@/features/projects/clientCapabilities';
import { useCreateProject } from '@/features/projects/hooks';
import {
  useCreateAndOpenProject as useCreateAndOpenProjectCore,
  useCreateProjectCore as useCreateProjectCoreImpl,
  useCreateWorkChatProject as useCreateWorkChatProjectImpl,
  type CreateProjectGuard,
  type ProjectCreationPorts,
  type UseCreateAndOpenProject,
  type UseCreateAndOpenProjectOptions,
  type UseCreateWorkChatProject,
} from '@heidi/agents/data';
import { clearLibraryDocumentPreview } from '@heidi/evidence';
import type { NavigatePort } from '@heidi/runtime/shell';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

/**
 * The app half of a Work create: the RPA capability probe, the library preview
 * the composer reset clears, and the create mutation bound to the chat sidebar.
 * Module-level, so the cores see one stable value.
 */
const projectCreationPorts: ProjectCreationPorts = {
  useCreateProject,
  getClientCapabilities: getProjectClientCapabilities,
  clearLibraryDocumentPreview,
};

export const useCreateProjectCore = (
  args: Parameters<typeof useCreateProjectCoreImpl>[0]
): ReturnType<typeof useCreateProjectCoreImpl> =>
  useCreateProjectCoreImpl(args, projectCreationPorts);

export const useCreateWorkChatProject = (options?: {
  guard?: CreateProjectGuard;
}): UseCreateWorkChatProject =>
  useCreateWorkChatProjectImpl(options, projectCreationPorts);

/** The Work-home create, navigating with the Next router once the project exists. */
export const useCreateAndOpenProject = (
  options?: UseCreateAndOpenProjectOptions
): UseCreateAndOpenProject => {
  const router = useRouter();
  // The router's boolean is not part of the port; it is awaited and dropped.
  const onNavigate = useCallback<NavigatePort>(
    async (href) => {
      await router.push(href);
    },
    [router]
  );
  return useCreateAndOpenProjectCore({ ...options, onNavigate }, projectCreationPorts);
};
